import { formatDistance } from './distance';
import { formatHms } from './dailyDate';
import { cityDailyScore, formatDailyAverageScore } from './score';

/**
 * Share-text builder for daily modes. Wordle-style: one short
 * compositional line plus a row of emoji + score that conveys the
 * result at a glance.
 *
 * Score-based palette (display surfaces are now score-keyed, not
 * distance-keyed):
 *   ≥98  → 🌎/🌍/🌏 (random globe — perfect band, geography-themed)
 *   ≥90  → 🟢  green
 *   ≥80  → 🟡  yellow
 *   ≥21  → 🔴  red
 *   ≤20  → 🌑  new moon (a "we don't talk about that round" marker
 *               for the way-off guesses; reads as visually distinct
 *               from the colored circles so a low-score round is
 *               obvious at a glance in the share grid).
 *
 * The perfect band rotates through the three Earth-globe emojis based
 * on the optional seed so a row of perfect rounds reads with visible
 * variety instead of five identical circles. Hashing on the seed makes
 * the choice deterministic — same seed → same globe across renders so
 * the leaderboard / share / stats all stay stable across refreshes.
 * The seed is whatever stable identifier the call site has handy: the
 * round index in a single-game grid, `${date}-${i}` for a row of
 * different days, etc. Fallback seed of 0 just always picks 🌎.
 */
const GLOBE_EMOJI_BAND = ['🌎', '🌍', '🌏'] as const;

export function scoreBandEmoji(score: number, seed: string | number = 0): string {
  if (score >= 98) return GLOBE_EMOJI_BAND[hashSeedToIndex(seed, GLOBE_EMOJI_BAND.length)];
  if (score >= 90) return '🟢';
  if (score >= 80) return '🟡';
  if (score >= 21) return '🔴';
  return '🌑';
}

/**
 * Tiny djb2-style string hash, modulo `n`. Fine for picking among a
 * 3-element list (the globe palette) — uniformity across small input
 * spaces is good enough; we're not cryptographic. Numeric seeds skip
 * the loop entirely for speed.
 */
function hashSeedToIndex(seed: string | number, n: number): number {
  if (typeof seed === 'number') return Math.abs(Math.trunc(seed)) % n;
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) >>> 0;
  return h % n;
}

/**
 * Rich + plain shareable content. Pasting into Slack / Google Chat /
 * Gmail picks up the HTML so "AtlasGuess.com" lands as a clickable
 * link to the specific mode URL. Plain text is the fallback for
 * surfaces that don't support rich paste.
 */
export interface ShareContent {
  text: string;
  html: string;
}

/**
 * Build the shareable content for a country quiz run.
 *
 *   AtlasGuess.com · Africa Countries Quiz
 *   4:32 · 47 / 53 (89%)
 *
 * `AtlasGuess.com` is the link text; href points at the specific quiz
 * URL so a recipient lands directly on the same mode. Quiz isn't
 * reproducible-by-date like Daily — the share is more of a "look how
 * I did" boast than a "play this puzzle" prompt.
 *
 * The accuracy fraction reflects first-attempt-correct only (the same
 * stat shown on the end screen), but we don't spell that out in the
 * share text — keeps it tight + lets the number speak for itself.
 */
export function buildCountryQuizShareContent(
  quizName: string,
  elapsedMs: number,
  firstTryCorrect: number,
  total: number,
  modeUrl: string,
): ShareContent {
  const accuracyPct = total === 0 ? 0 : Math.round((firstTryCorrect / total) * 100);
  const stats = `${formatHms(elapsedMs)} · ${firstTryCorrect} / ${total} (${accuracyPct}%)`;
  // Plain-text variant includes the bare URL in parens so the link is
  // always present even on no-rich-paste surfaces.
  const text = `AtlasGuess.com (${modeUrl}) · ${quizName}\n${stats}`;
  // HTML variant uses an anchor — the link text says "AtlasGuess.com",
  // the href is the full mode URL. <br> keeps the two-line layout.
  const html = `<a href="${escapeHtml(modeUrl)}">AtlasGuess.com</a> · ${escapeHtml(quizName)}<br>${escapeHtml(stats)}`;
  return { text, html };
}

/**
 * Build the share content for a daily-cities run. The link points at
 * the bare site origin (not the daily mode URL) — daily lives on `/`,
 * so a recipient who clicks lands on today's puzzle for them
 * automatically (date is local-resolved on each user's load).
 *
 * Two-line layout — header, then grid + total separated by an em
 * dash on a single second line:
 *
 *   AtlasGuess.com · Apr 29
 *   🔵 100  🟢 88  🟡 71  🔴 22  🔵 99 — 76.00 · avg 472 mi
 *
 * Score grid carries the per-round scores after each emoji so a
 * reader can read accuracy at a glance without resolving km. The
 * total reads the run's average score (0..100, fixed-2 dp) — one
 * clean "out of 100" number rather than a sum out of N×100. The
 * avg distance suffix keeps the underlying signal visible for
 * anyone curious about the raw spatial precision.
 *
 * Empty-distances case (defensive) skips the dash separator so the
 * line reads "— · avg 0 mi" rather than " — — · avg 0 mi".
 *
 * Plain text omits the bare URL entirely so pastes stay tight; HTML
 * carries the clickable link via the "AtlasGuess.com" anchor.
 */
export function buildCityDailyShareContent(
  distancesKm: readonly number[],
  dateLabel: string,
  unitSystem: 'miles' | 'km',
  siteUrl: string,
): ShareContent {
  const scores = distancesKm.map(cityDailyScore);
  const totalScore = scores.reduce((sum, s) => sum + s, 0);

  // Per-round cell: emoji + score (e.g. "🌎 100"). Joined with two
  // spaces so the row reads as comfortably-spaced groups rather than
  // a tight string of pairs. Seed = round index so the globe rotation
  // matches what's shown on the leaderboard / end-screen for the same
  // run.
  const grid = scores
    .map((s, i) => `${scoreBandEmoji(s, i)} ${s}`)
    .join('  ');

  const avgKm = distancesKm.length === 0
    ? 0
    : distancesKm.reduce((sum, km) => sum + km, 0) / distancesKm.length;
  const avgLabel = formatDistance(avgKm, unitSystem);

  // Score line: 0..100 average per round, fixed-2 dp. Replaces the
  // old "{total} / {N×100}" framing — a single 0..100 number reads
  // more naturally than a fraction the player has to mentally
  // normalize.
  const scoreLabel = formatDailyAverageScore(totalScore, scores.length);
  const totalLine = `${scoreLabel} · avg ${avgLabel}`;
  // Grid + total on a single line separated by " — " (em dash with
  // breathing-space). Skip the dash entirely when grid is empty
  // (zero-rounds defensive path) so we don't render a stray
  // " — " before the total.
  const secondLine = grid ? `${grid} — ${totalLine}` : totalLine;
  const text = `AtlasGuess.com · ${dateLabel}\n${secondLine}`;
  const html = `<a href="${escapeHtml(siteUrl)}">AtlasGuess.com</a> · ${escapeHtml(dateLabel)}<br>${escapeHtml(secondLine)}`;
  return { text, html };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Copy plain text OR rich {text, html} content to the clipboard. For
 * rich content, uses ClipboardItem with both `text/plain` and `text/html`
 * MIME types so paste targets pick the format they prefer (Slack/Gmail
 * → HTML; terminal → plain). Falls back to plain text on browsers
 * without ClipboardItem support, then to the legacy textarea+execCommand
 * path for ancient surfaces.
 */
export async function copyToClipboard(content: string | ShareContent): Promise<boolean> {
  const isRich = typeof content !== 'string';
  const text = isRich ? content.text : content;

  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    if (isRich && typeof ClipboardItem !== 'undefined') {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([content.html], { type: 'text/html' }),
            'text/plain': new Blob([content.text], { type: 'text/plain' }),
          }),
        ]);
        return true;
      } catch {
        // Some browsers reject specific MIME types or require user-
        // gesture priming differently — fall through to writeText.
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  if (typeof document === 'undefined') return false;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}
